import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const field = "mt-1.5 bg-white/5 border-white/10";
const move = (arr, i, dir) => { const j = i + dir; if (j < 0 || j >= arr.length) return arr; const next = [...arr]; [next[i], next[j]] = [next[j], next[i]]; return next; };

const COPY = [
  { key: "contact", title: "Contact page", href: "/contact", fields: [["contact_eyebrow", "Eyebrow"], ["contact_title", "Title"], ["contact_intro", "Intro paragraph", "textarea"], ["contact_form_title", "Form title"]] },
  { key: "advertise", title: "Advertise page", href: "/advertise", fields: [["advertise_eyebrow", "Eyebrow"], ["advertise_title", "Title"], ["advertise_intro", "Intro paragraph", "textarea"], ["advertise_form_title", "Form title"]] },
];

const ListEditor = ({ items, onChange, titleKey = "title", textKey = "text", testPrefix, addLabel, rows = 4 }) => {
  const setRow = (i, k, v) => onChange(items.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  return (
    <div className="space-y-3">
      {items.map((p, i) => (
        <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-4" data-testid={`${testPrefix}-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={p[titleKey]} onChange={(e) => setRow(i, titleKey, e.target.value)} placeholder="Heading" className="bg-white/5 border-white/10 font-semibold" data-testid={`${testPrefix}-title-${i}`} />
            <button type="button" onClick={() => onChange(move(items, i, -1))} disabled={i === 0} className="p-1.5 text-slate-400 hover:text-white disabled:opacity-25" data-testid={`${testPrefix}-up-${i}`}><ArrowUp size={14} /></button>
            <button type="button" onClick={() => onChange(move(items, i, 1))} disabled={i === items.length - 1} className="p-1.5 text-slate-400 hover:text-white disabled:opacity-25" data-testid={`${testPrefix}-down-${i}`}><ArrowDown size={14} /></button>
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="p-1.5 text-slate-400 hover:text-rose-400" data-testid={`${testPrefix}-delete-${i}`}><Trash2 size={14} /></button>
          </div>
          <Textarea value={p[textKey]} onChange={(e) => setRow(i, textKey, e.target.value)} rows={rows} className="mt-2 bg-white/5 border-white/10 text-sm" data-testid={`${testPrefix}-text-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...items, { [titleKey]: "", [textKey]: "" }])} className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid={`${testPrefix}-add`}><Plus size={14} className="mr-1" /> {addLabel}</Button>
    </div>
  );
};

export default function PagesAdmin() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh } = useSettings();

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const pages = settings?.pages || {};
  const setPage = (k, v) => setSettings((s) => ({ ...s, pages: { ...s.pages, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", settings); setSettings(data); refresh(); toast.success("Pages saved"); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };

  if (!settings) return null;
  return (
    <div data-testid="admin-pages">
      <p className="eyebrow mb-2">Content</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Pages</h1>
      <p className="mt-2 text-sm text-slate-400 mb-8">Privacy Policy wording plus the copy on the Contact and Advertise pages.</p>
      <form onSubmit={save} className="space-y-6 max-w-3xl">
        <section className="card-surface rounded-2xl p-6" data-testid="pages-section-privacy">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div><p className="eyebrow mb-1">Privacy Policy</p><p className="text-xs text-slate-500">Each block is a heading and its paragraph. Contact email comes from the notification email on the Homepage page.</p></div>
            <Link to="/privacy" target="_blank" className="text-xs text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 shrink-0" data-testid="pages-privacy-preview">Preview <ExternalLink size={12} /></Link>
          </div>
          <div className="sm:w-1/2 mb-4"><Label>“Last updated” label</Label><Input value={pages.privacy_updated || ""} onChange={(e) => setPage("privacy_updated", e.target.value)} className={field} data-testid="pages-privacy-updated-input" /></div>
          <ListEditor items={pages.privacy_sections || []} onChange={(v) => setPage("privacy_sections", v)} testPrefix="pages-privacy-section" addLabel="Add section" />
        </section>

        {COPY.map((sec) => (
          <section key={sec.key} className="card-surface rounded-2xl p-6" data-testid={`pages-section-${sec.key}`}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <p className="eyebrow">{sec.title}</p>
              <Link to={sec.href} target="_blank" className="text-xs text-sky-400 hover:text-sky-300 inline-flex items-center gap-1" data-testid={`pages-${sec.key}-preview`}>Preview <ExternalLink size={12} /></Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {sec.fields.map(([id, label, type]) => (
                <div key={id} className={type === "textarea" ? "sm:col-span-2" : ""}>
                  <Label>{label}</Label>
                  {type === "textarea" ? <Textarea value={pages[id] || ""} onChange={(e) => setPage(id, e.target.value)} rows={3} className={field} data-testid={`pages-${id}-input`} /> : <Input value={pages[id] || ""} onChange={(e) => setPage(id, e.target.value)} className={field} data-testid={`pages-${id}-input`} />}
                </div>
              ))}
            </div>
            {sec.key === "advertise" && (
              <div className="mt-5"><p className="eyebrow mb-3">Advertise perks</p><ListEditor items={pages.advertise_perks || []} onChange={(v) => setPage("advertise_perks", v)} testPrefix="pages-advertise-perk" addLabel="Add perk" rows={2} /></div>
            )}
          </section>
        ))}

        <Button type="submit" disabled={saving} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="pages-save-button">{saving ? "Saving…" : "Save pages"}</Button>
      </form>
    </div>
  );
}
